import React, { useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Copy, Check, Terminal, Code2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const TOOLS = [
  { id: "python", label: "Python", icon: Code2 },
  { id: "curl", label: "cURL", icon: Terminal },
  { id: "cursor", label: "Cursor", icon: ExternalLink },
  { id: "any", label: "Any Tool", icon: ExternalLink },
];

const SNIPPETS = {
  python: `from openai import OpenAI

client = OpenAI(
    api_key="YOUR_QNNEAL_KEY",
    base_url="https://api.qnneal.dev/v1"
)

response = client.chat.completions.create(
    model="gpt-4o",
    messages=[{"role": "user", "content": "Hello!"}]
)
print(response.choices[0].message.content)`,

  curl: `curl https://api.qnneal.dev/v1/chat/completions \\
  -H "Authorization: Bearer YOUR_QNNEAL_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "gpt-4o",
    "messages": [{"role": "user", "content": "Hello!"}]
  }'`,

  cursor: `1. Open Cursor Settings (Cmd/Ctrl + ,)
2. Navigate to Models > OpenAI API Key
3. Paste your QNNEAL key: qn_live_xxxx...
4. Set Override API Base URL:
   https://api.qnneal.dev/v1
5. Save. Every Cursor request now routes
   through QNNEAL automatically.`,

  any: `For any OpenAI-compatible tool:

1. Find the API settings in your tool
2. Set Base URL: https://api.qnneal.dev/v1
3. Set API Key:  your qn_live_xxxx key
4. Save

That's it. QNNEAL optimizes every request
in the background. No code changes needed.`,
};

export default function ConnectToolsGuide({ proxyKeyPrefix }) {
  const [selected, setSelected] = useState("python");
  const [copied, setCopied] = useState(false);

  const copySnippet = () => {
    const text = SNIPPETS[selected].replace("YOUR_QNNEAL_KEY", proxyKeyPrefix || "qn_live_xxxxxxxxxxxx");
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Copied to clipboard!");
  };

  return (
    <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="connect-tools-guide" data-automation-id="connect-tools-guide">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Terminal className="w-5 h-5 text-[var(--qn-secondary)]" />
          Connect Your Tools
        </CardTitle>
        <p className="text-sm text-[var(--qn-foreground-muted)]">
          Replace your OpenAI base URL with QNNEAL's. Every request is automatically optimized.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Tool selector */}
        <div className="flex gap-2" data-testid="tool-selector" data-automation-id="tool-selector">
          {TOOLS.map((tool) => (
            <button
              key={tool.id}
              onClick={() => setSelected(tool.id)}
              data-testid={`tool-${tool.id}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                selected === tool.id
                  ? "bg-[var(--qn-primary)] text-white"
                  : "bg-white/[0.08] text-slate-300 border border-white/[0.15] hover:bg-white/[0.14] hover:text-slate-200"
              }`}
            >
              <tool.icon className="w-3.5 h-3.5" />
              {tool.label}
            </button>
          ))}
        </div>

        {/* Code snippet */}
        <div className="relative">
          <pre className="bg-[var(--qn-background)] border border-[var(--qn-border)] rounded-lg p-4 text-sm font-mono text-[var(--qn-foreground)] overflow-x-auto whitespace-pre leading-relaxed" data-testid="tool-snippet" data-automation-id="tool-snippet">
            {SNIPPETS[selected]}
          </pre>
          <Button
            size="sm"
            variant="ghost"
            className="absolute top-2 right-2 h-7 text-xs"
            onClick={copySnippet}
            data-testid="copy-snippet-btn" data-automation-id="copy-snippet-btn"
          >
            {copied ? <Check className="w-3.5 h-3.5 mr-1 text-emerald-400" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>

        <p className="text-xs text-[var(--qn-foreground-subtle)]">
          QNNEAL intercepts every request, finds the optimal provider, and returns the result in OpenAI format. Your tools work exactly the same — just more optimized and faster.
        </p>
      </CardContent>
    </Card>
  );
}
